import React, { useState, useEffect } from 'react';
import {
    Alert,
    Box,
    Typography,
    Button,
    IconButton,
    Collapse
} from '@mui/material';
import {
    Videocam as VideocamIcon,
    VideocamOff as VideocamOffIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    ArrowDropDown as ExpandIcon,
    ArrowDropUp as CollapseIcon
} from '@mui/icons-material';
import { checkMediaPermissions, getPermissionInstructions } from '../utils/mediaPermissions';

/**
 * Component for displaying media permission status and instructions
 */
const MediaPermissionsHelper = ({ onPermissionChange, variant = 'standard' }) => {
    const [permissionStatus, setPermissionStatus] = useState({
        camera: 'unknown',
        microphone: 'unknown'
    });
    const [expanded, setExpanded] = useState(false);
    const [permissionError, setPermissionError] = useState(null);
    const [instructions, setInstructions] = useState(null);

    useEffect(() => {
        checkPermissions();
        // Get browser-specific instructions
        setInstructions(getPermissionInstructions());
    }, []);

    const checkPermissions = async () => {
        const result = await checkMediaPermissions();

        setPermissionStatus({
            camera: result.permissions.camera,
            microphone: result.permissions.microphone
        });

        setPermissionError(result.errorMessage);

        if (onPermissionChange) {
            onPermissionChange(result);
        }
    };

    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    const hasPermissionIssues = permissionStatus.camera !== 'granted' ||
        permissionStatus.microphone !== 'granted';

    // Simple version just shows an alert if there are issues
    if (variant === 'minimal' && !hasPermissionIssues) {
        return null;
    }

    if (variant === 'minimal' && hasPermissionIssues) {
        return (
            <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                    <Button
                        color="inherit"
                        size="small"
                        onClick={checkPermissions}
                    >
                        Check Again
                    </Button>
                }
            >
                Media permission issues detected. Video and audio features may not work.
            </Alert>
        );
    }

    return (
        <Box sx={{ mb: 2, width: '100%' }}>
            {/* Status indicators */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#1a1a1a',
                borderRadius: 1,
                p: 1,
                mb: hasPermissionIssues ? 1 : 0
            }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {permissionStatus.camera === 'granted' ? (
                            <VideocamIcon color="success" fontSize="small" />
                        ) : (
                            <VideocamOffIcon color="error" fontSize="small" />
                        )}
                        <Typography variant="caption">
                            Camera: {permissionStatus.camera === 'granted' ? 'Allowed' :
                                permissionStatus.camera === 'denied' ? 'Blocked' :
                                    permissionStatus.camera === 'prompt' ? 'Ask' : 'Unknown'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {permissionStatus.microphone === 'granted' ? (
                            <MicIcon color="success" fontSize="small" />
                        ) : (
                            <MicOffIcon color="error" fontSize="small" />
                        )}
                        <Typography variant="caption">
                            Microphone: {permissionStatus.microphone === 'granted' ? 'Allowed' :
                                permissionStatus.microphone === 'denied' ? 'Blocked' :
                                    permissionStatus.microphone === 'prompt' ? 'Ask' : 'Unknown'}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={checkPermissions}
                    >
                        Check Again
                    </Button>

                    {hasPermissionIssues && (
                        <IconButton size="small" onClick={toggleExpanded}>
                            {expanded ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Help content */}
            {hasPermissionIssues && (
                <Collapse in={expanded}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            How to fix permission issues:
                        </Typography>

                        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                            <ol style={{ paddingLeft: '1.5rem', margin: 0 }}>
                                {instructions?.browser.map((instruction, index) => (
                                    <li key={index}>{instruction}</li>
                                ))}
                            </ol>
                        </Typography>

                        <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                            <strong>Note:</strong> {instructions?.general[0]}
                        </Typography>
                    </Alert>
                </Collapse>
            )}
        </Box>
    );
};

export default MediaPermissionsHelper;
